import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const Footer = () => {
  const { t } = useTranslation();
  
  return (
    <FooterContainer>
      <FooterContent>
        <FooterColumn>
          <FooterLogo>
            <LogoImg src="/assets/icon.png" alt="Xenya" />
            <LogoText>Xenya</LogoText>
          </FooterLogo>
          <FooterText>
            {t('footer.description')}
          </FooterText>
          <SocialLinks>
            <SocialIcon href="https://instagram.com/xenya" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-instagram"></i>
            </SocialIcon>
            <SocialIcon href="https://twitter.com/xenya" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-twitter"></i>
            </SocialIcon>
            <SocialIcon href="https://facebook.com/xenya" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-facebook-f"></i>
            </SocialIcon>
            <SocialIcon href="https://linkedin.com/company/xenya" target="_blank" rel="noopener noreferrer">
              <i className="fab fa-linkedin-in"></i>
            </SocialIcon>
          </SocialLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterHeading>{t('footer.headers.legal')}</FooterHeading>
          <FooterLinks>
            <FooterLink to="/terms">{t('footer.links.terms')}</FooterLink>
            <FooterLink to="/privacy">{t('footer.links.privacy')}</FooterLink>
            <FooterLink to="/eula">EULA</FooterLink>
            <FooterLink to="/rgpd">RGPD</FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterHeading>{t('footer.headers.support')}</FooterHeading>
          <FooterLinks>
            <FooterLink to="/support">{t('footer.links.helpCenter')}</FooterLink>
            <FooterLink to="/support#faq">{t('footer.links.faq')}</FooterLink>
            <FooterLink to="/support#contact">{t('footer.links.contactUs')}</FooterLink>
            <FooterLink href="mailto:support@xenya.com">support@xenya.com</FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        <FooterColumn>
          <FooterHeading>{t('footer.headers.company')}</FooterHeading>
          <FooterLinks>
            <FooterLink to="/about">{t('footer.links.aboutUs')}</FooterLink>
            <FooterLink to="/careers">{t('footer.links.careers')}</FooterLink>
            <FooterLink to="/press">{t('footer.links.press')}</FooterLink>
            <FooterLink to="/blog">{t('footer.links.blog')}</FooterLink>
          </FooterLinks>
        </FooterColumn>
      </FooterContent>
      
      <FooterBottom>
        <Copyright>{t('legal.copyright')}</Copyright>
        <StoreLinks>
          <StoreLink href="https://apps.apple.com" target="_blank" rel="noopener noreferrer">
            {t('footer.stores.appStore')}
          </StoreLink>
          <StoreLink href="https://play.google.com" target="_blank" rel="noopener noreferrer">
            {t('footer.stores.googlePlay')}
          </StoreLink>
        </StoreLinks>
      </FooterBottom>
    </FooterContainer>
  );
};

// Styled Components
const FooterContainer = styled.footer`
  background: ${({ theme }) => theme.colors.secondary};
  padding: 60px 0 20px;
  color: ${({ theme }) => theme.colors.light};
`;

const FooterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  margin-bottom: 40px;
`;

const FooterColumn = styled.div`
  flex: 1;
  min-width: 200px;
  margin-bottom: 30px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-basis: 50%;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-basis: 100%;
  }
`;

const FooterLogo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`;

const LogoImg = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 8px;
`;

const LogoText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-left: 10px;
`;

const FooterText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.gray};
  line-height: 1.6;
  margin-bottom: 20px;
  max-width: 300px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.light};
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: ${({ theme }) => theme.colors.accent};
    transform: translateY(-3px);
  }
`;

const FooterHeading = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: 20px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 2px;
    background: ${({ theme }) => theme.colors.accent};
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.gray};
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    transform: translateX(5px);
  }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 20px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    gap: 15px;
  }
`;

const Copyright = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.gray};
`;

const StoreLinks = styled.div`
  display: flex;
  gap: 20px;
`;

const StoreLink = styled.a`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.gray};
  text-decoration: none;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

export default Footer;